import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../config/queryClient';
import { updateTaskDetails } from 'services/CaseServices';
import { getTenantDetails } from 'utils/utils';
import { logger } from '../../../utils/logger';

export const useUpdateSubtaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, subTaskIndex, checked, parentTask, taskStages, columnId }) => {
      const [tenantId] = getTenantDetails();

      logger.log('Updating subtask:', { parentTask, subTaskIndex, checked });
      // Update the subtask status using index
      const updatedSubTasks = parentTask?.sub_task?.map((st, index) => (index === subTaskIndex ? { ...st, sub_task_status: checked } : st));

      // Count completed subtasks BEFORE and AFTER
      const previousCompletedCount = parentTask?.sub_task?.filter((st) => st.sub_task_status === true).length;
      const newCompletedCount = updatedSubTasks?.filter((st) => st.sub_task_status === true).length;
      const totalCount = updatedSubTasks?.length;

      // Determine target stage based on completion
      let targetStageId = null;
      let shouldMoveStage = false;

      if (newCompletedCount === 0) {
        // No subtasks completed → move back to original stage or To Do
        if (parentTask?.dynamic_task_stage) {
          // Restore original stage if available
          targetStageId = parentTask?.dynamic_task_stage;
        } else {
          // Otherwise move to To Do
          const toDoStage = taskStages?.find((stage) => stage.task_stage_name?.toLowerCase() === 'to_do');
          targetStageId = toDoStage?.task_stage_id;
        }
        shouldMoveStage = targetStageId && targetStageId !== columnId;
      } else if (newCompletedCount === totalCount) {
        // All subtasks completed → move to Done
        const doneStage = taskStages?.find((stage) => stage.task_stage_name?.toLowerCase() === 'done');
        targetStageId = doneStage?.task_stage_id;
        shouldMoveStage = targetStageId && targetStageId !== columnId;
      } else if (previousCompletedCount === 0 && newCompletedCount > 0) {
        // First subtask completed → move to In Progress and store original stage
        const inProgressStage = taskStages?.find((stage) => stage.task_stage_name?.toLowerCase() === 'in_progress');
        targetStageId = inProgressStage?.task_stage_id;
        shouldMoveStage = targetStageId && targetStageId !== columnId;
      } else if (previousCompletedCount === totalCount && newCompletedCount < totalCount) {
        // Unchecked from Done → move to In Progress
        const inProgressStage = taskStages?.find((stage) => stage.task_stage_name?.toLowerCase() === 'in_progress');
        targetStageId = inProgressStage?.task_stage_id;
        shouldMoveStage = targetStageId && targetStageId !== columnId;
      }

      // Prepare update data
      const updateData = {
        sk: `task#${parentTask?.task_id}`,
        pk: `${tenantId}#${parentTask?.task_id}`,
        sub_task: updatedSubTasks,
        task_id: parentTask?.task_id,
        due_date: parentTask?.due_date,
        assign_to: parentTask?.assign_to,
        task_stage: parentTask?.task_stage,
      };

      // Store the original stage when first moving to in progress
      if (shouldMoveStage && !parentTask.dynamic_task_stage && targetStageId) {
        const inProgressStage = taskStages?.find((stage) => stage.task_stage_name?.toLowerCase() === 'in_progress');
        if (targetStageId === inProgressStage?.task_stage_id) {
          updateData.dynamic_task_stage = columnId;
        }
      }

      // Add stage change if needed
      if (shouldMoveStage && targetStageId) {
        updateData.task_stage = targetStageId;
      }

      // Single API call to update subtask and move stage
      const response = await updateTaskDetails(taskId, null, updateData, false, false, null, true);

      return {
        ...response.data,
        updatedSubTasks,
        targetStageId: shouldMoveStage ? targetStageId : columnId,
        sourceColumnId: columnId,
      };
    },
    onMutate: async ({ taskId, subTaskIndex, checked, parentTask, columnId, taskStages }) => {
      // Calculate the updated subtasks using index
      const updatedSubTasks = parentTask?.sub_task?.map((st, index) => (index === subTaskIndex ? { ...st, sub_task_status: checked } : st));

      // Count completed subtasks BEFORE and AFTER
      const previousCompletedCount = parentTask?.sub_task?.filter((st) => st.sub_task_status === true).length;
      const newCompletedCount = updatedSubTasks?.filter((st) => st.sub_task_status === true).length;
      const totalCount = updatedSubTasks?.length;

      // Determine target stage based on completion (same logic as mutationFn)
      let targetStageId = columnId; // Default to current column
      let shouldMoveStage = false;

      if (newCompletedCount === 0) {
        // No subtasks completed → move back to original stage or To Do
        if (parentTask?.dynamic_task_stage) {
          targetStageId = parentTask?.dynamic_task_stage;
        } else {
          const toDoStage = taskStages?.find((stage) => stage.task_stage_name?.toLowerCase() === 'to_do');
          targetStageId = toDoStage?.task_stage_id || columnId;
        }
        shouldMoveStage = targetStageId && targetStageId !== columnId;
      } else if (newCompletedCount === totalCount) {
        // All subtasks completed → move to Done
        const doneStage = taskStages?.find((stage) => stage.task_stage_name?.toLowerCase() === 'done');
        targetStageId = doneStage?.task_stage_id || columnId;
        shouldMoveStage = targetStageId && targetStageId !== columnId;
      } else if (previousCompletedCount === 0 && newCompletedCount > 0) {
        // First subtask completed → move to In Progress
        const inProgressStage = taskStages?.find((stage) => stage.task_stage_name?.toLowerCase() === 'in_progress');
        targetStageId = inProgressStage?.task_stage_id || columnId;
        shouldMoveStage = targetStageId && targetStageId !== columnId;
      } else if (previousCompletedCount === totalCount && newCompletedCount < totalCount) {
        // Unchecked from Done → move to In Progress
        const inProgressStage = taskStages?.find((stage) => stage.task_stage_name?.toLowerCase() === 'in_progress');
        targetStageId = inProgressStage?.task_stage_id || columnId;
        shouldMoveStage = targetStageId && targetStageId !== columnId;
      }

      // Cancel queries for affected columns
      await queryClient.cancelQueries({ queryKey: [...queryKeys.tasks.column(columnId)] });
      if (shouldMoveStage && targetStageId) {
        await queryClient.cancelQueries({ queryKey: [...queryKeys.tasks.column(targetStageId)] });
      }

      // Snapshot previous data for both columns
      const previousSourceData = queryClient.getQueriesData({ queryKey: [...queryKeys.tasks.column(columnId)] });
      const previousTargetData =
        shouldMoveStage && targetStageId ? queryClient.getQueriesData({ queryKey: [...queryKeys.tasks.column(targetStageId)] }) : null;

      // Optimistically update the UI
      if (shouldMoveStage && targetStageId) {
        // CASE 1: Moving to a different stage

        // Remove from source column
        queryClient.setQueriesData({ queryKey: [...queryKeys.tasks.column(columnId)] }, (old) => {
          if (!old?.pages) return old;

          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items?.filter((task) => task.task_id !== taskId) || [],
              totalCount: Math.max(0, (page.totalCount || 0) - 1),
            })),
          };
        });

        // Add to target column with updated task
        queryClient.setQueriesData({ queryKey: [...queryKeys.tasks.column(targetStageId)] }, (old) => {
          if (!old?.pages || !old.pages.length) {
            // Create initial structure if it doesn't exist
            return {
              pages: [
                {
                  items: [
                    {
                      ...parentTask,
                      sub_task: updatedSubTasks,
                      task_stage: targetStageId,
                    },
                  ],
                  page: 1,
                  hasMore: false,
                  totalCount: 1,
                },
              ],
              pageParams: [1],
            };
          }

          return {
            ...old,
            pages: old.pages.map((page, index) => {
              if (index === 0) {
                // Add to first page
                return {
                  ...page,
                  items: [
                    {
                      ...parentTask,
                      sub_task: updatedSubTasks,
                      task_stage: targetStageId,
                    },
                    ...page.items,
                  ],
                  totalCount: (page.totalCount || 0) + 1,
                };
              }
              return page;
            }),
          };
        });
      } else {
        // CASE 2: Staying in same column, just update subtasks
        queryClient.setQueriesData({ queryKey: [...queryKeys.tasks.column(columnId)] }, (old) => {
          if (!old?.pages) return old;

          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items?.map((task) => {
                if (task.task_id === taskId) {
                  // Update using index to ensure only the correct subtask is affected
                  const updatedTask = {
                    ...task,
                    sub_task: updatedSubTasks,
                  };
                  return updatedTask;
                }
                return task;
              }),
            })),
          };
        });
      }

      return {
        previousSourceData,
        previousTargetData,
        shouldMoveStage,
        targetStageId,
        sourceColumnId: columnId,
      };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousSourceData) {
        context.previousSourceData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousTargetData) {
        context.previousTargetData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      logger.error('Failed to update subtask:', err);
    },
    // onSuccess: (data, variables) => {
    //     // Invalidate source column queries
    //     queryClient.invalidateQueries({
    //         queryKey: [...queryKeys.tasks.column(variables.columnId)],
    //     });

    //     // If stage changed, also invalidate target column
    //     if (data.targetStageId && data.targetStageId !== variables.columnId) {
    //         queryClient.invalidateQueries({
    //             queryKey: [...queryKeys.tasks.column(data.targetStageId)],
    //         });
    //     }
    // },
  });
};
